// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { AnyAction } from 'redux';

import { BoundariesActionTypes } from 'actions/boundaries-actions';
import { AuthActionTypes } from 'actions/auth-actions';
import { SettingsActionTypes } from 'actions/settings-actions';
import { AnnotationActionTypes } from 'actions/annotation-actions';
import {
    SettingsState, GridColor, FrameSpeed, ColorBy, PlayerSettingsState,
} from 'reducers';
import { ObjectState, ShapeType, DimensionType } from 'cvat-core-wrapper';
import { clamp } from 'utils/math';
import { DEFAULT_ASINH_SOFTNESS, DEFAULT_GAMMA } from 'utils/sixteen-bit';

// The window is expressed in SOURCE units. On an ordinary frame the limit stays 255 exactly as
// before; on a 16-bit frame the viewer raises it to that camera's bit depth, so the sliders
// address camera counts rather than a rescaled copy of them. Clamping to a hard 255 here was what
// made a 16-bit window impossible to express, whatever the sliders offered.
//
// Brightness and Contrast are the SAME window seen from the other side, exactly as in ImageJ's
// Brightness/Contrast dialog: the annotator sets how bright and how contrasty the RESULT is, and
// the window follows. Both therefore count OPPOSITE to the numbers they drive -- a brighter
// picture means a window centred LOWER (more of the data lands above it, i.e. white), and more
// contrast means a NARROWER window. Storing the window centre and width directly under those two
// labels, which is what this did before, made the Brightness slider darken the image as it was
// dragged to the right.
const recomputeRange = (minLevel: number, maxLevel: number, levelLimit: number): {
    minLevel: number; maxLevel: number; brightnessLevel: number; contrastLevel: number;
} => {
    const limit = Math.max(levelLimit, 1);
    const safeMin = clamp(minLevel, 0, limit - 1);
    const safeMax = clamp(Math.max(maxLevel, safeMin + 1), safeMin + 1, limit);
    return {
        minLevel: safeMin,
        maxLevel: safeMax,
        brightnessLevel: limit - (safeMin + safeMax) / 2,
        contrastLevel: limit - (safeMax - safeMin),
    };
};

/** The window a (brightness, contrast) pair asks for, in source units. Inverse of the above. */
const windowFor = (brightnessLevel: number, contrastLevel: number, levelLimit: number): {
    min: number; max: number;
} => {
    const limit = Math.max(levelLimit, 1);
    const centre = limit - brightnessLevel;
    const width = Math.max(limit - contrastLevel, 1);
    return { min: centre - width / 2, max: centre + width / 2 };
};

const defaultState: SettingsState = {
    shapes: {
        colorBy: ColorBy.LABEL,
        opacity: 3,
        selectedOpacity: 30,
        outlined: false,
        outlineColor: '#000000',
        showBitmap: false,
        showProjections: false,
        showGroundTruth: false,
    },
    workspace: {
        autoSave: false,
        autoSaveInterval: 15 * 60 * 1000,
        aamZoomMargin: 100,
        automaticBordering: false,
        showObjectsTextAlways: false,
        showAllInterpolationTracks: false,
        intelligentPolygonCrop: true,
        defaultApproxPolyAccuracy: 9,
        textFontSize: 14,
        controlPointsSize: 5,
        textPosition: 'auto',
        textContent: 'id,source,label,attributes,descriptions',
        toolsBlockerState: {
            algorithmsLocked: false,
            buttonVisible: false,
        },
        showTagsOnFrame: true,
    },
    player: {
        canvasBackgroundColor: '#ffffff',
        frameStep: 10,
        frameSpeed: FrameSpeed.Usual,
        resetZoom: false,
        rotateAll: false,
        smoothImage: true,
        showDeletedFrames: false,
        grid: false,
        gridSize: 100,
        gridColor: GridColor.White,
        gridOpacity: 100,
        // The full 0..255 window: centred at 127.5 (brightness 255 - 127.5) and as wide as the
        // scale gets (contrast 255 - 255), i.e. nothing clipped at either end.
        brightnessLevel: 127.5,
        contrastLevel: 0,
        minLevel: 0,
        maxLevel: 255,
        displayCurve: 'linear',
        gammaLevel: DEFAULT_GAMMA,
        asinhSoftness: DEFAULT_ASINH_SOFTNESS,
        levelLimit: 255,
    },
    imageFilters: [],
    showDialog: false,
};

export default (state = defaultState, action: AnyAction): SettingsState => {
    switch (action.type) {
        case SettingsActionTypes.SWITCH_ROTATE_ALL: {
            return {
                ...state,
                player: {
                    ...state.player,
                    rotateAll: action.payload.rotateAll,
                },
            };
        }
        case SettingsActionTypes.SWITCH_GRID: {
            return {
                ...state,
                player: {
                    ...state.player,
                    grid: action.payload.grid,
                },
            };
        }
        case SettingsActionTypes.CHANGE_GRID_SIZE: {
            return {
                ...state,
                player: {
                    ...state.player,
                    gridSize: action.payload.gridSize,
                },
            };
        }
        case SettingsActionTypes.CHANGE_GRID_COLOR: {
            return {
                ...state,
                player: {
                    ...state.player,
                    gridColor: action.payload.gridColor,
                },
            };
        }
        case SettingsActionTypes.CHANGE_GRID_OPACITY: {
            return {
                ...state,
                player: {
                    ...state.player,
                    gridOpacity: action.payload.gridOpacity,
                },
            };
        }
        case SettingsActionTypes.CHANGE_SHAPES_COLOR_BY: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    colorBy: action.payload.colorBy,
                },
            };
        }
        case SettingsActionTypes.CHANGE_SHOW_GROUND_TRUTH: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    showGroundTruth: action.payload.showGroundTruth,
                },
            };
        }
        case SettingsActionTypes.CHANGE_SHAPES_OPACITY: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    opacity: action.payload.opacity,
                },
            };
        }
        case SettingsActionTypes.CHANGE_SELECTED_SHAPES_OPACITY: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    selectedOpacity: action.payload.selectedOpacity,
                },
            };
        }
        case SettingsActionTypes.CHANGE_SHAPES_OUTLINED_BORDERS: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    outlined: action.payload.outlined,
                    outlineColor: action.payload.color,
                },
            };
        }
        case SettingsActionTypes.CHANGE_SHAPES_SHOW_PROJECTIONS: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    showProjections: action.payload.showProjections,
                },
            };
        }
        case SettingsActionTypes.CHANGE_SHOW_UNLABELED_REGIONS: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    showBitmap: action.payload.showBitmap,
                },
            };
        }
        case SettingsActionTypes.CHANGE_FRAME_STEP: {
            return {
                ...state,
                player: {
                    ...state.player,
                    frameStep: action.payload.frameStep,
                },
            };
        }
        case SettingsActionTypes.CHANGE_FRAME_SPEED: {
            return {
                ...state,
                player: {
                    ...state.player,
                    frameSpeed: action.payload.frameSpeed,
                },
            };
        }
        case SettingsActionTypes.SWITCH_RESET_ZOOM: {
            return {
                ...state,
                player: {
                    ...state.player,
                    resetZoom: action.payload.resetZoom,
                },
            };
        }
        case SettingsActionTypes.SWITCH_SMOOTH_IMAGE: {
            return {
                ...state,
                player: {
                    ...state.player,
                    smoothImage: action.payload.smoothImage,
                },
            };
        }
        case SettingsActionTypes.SWITCH_TEXT_FONT_SIZE: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    textFontSize: action.payload.fontSize,
                },
            };
        }
        case SettingsActionTypes.SWITCH_CONTROL_POINTS_SIZE: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    controlPointsSize: action.payload.controlPointsSize,
                },
            };
        }
        case SettingsActionTypes.SWITCH_TEXT_POSITION: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    textPosition: action.payload.position,
                },
            };
        }
        case SettingsActionTypes.SWITCH_TEXT_CONTENT: {
            const { textContent } = action.payload;
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    textContent,
                },
            };
        }
        case SettingsActionTypes.CHANGE_BRIGHTNESS_LEVEL: {
            const { levelLimit } = state.player;
            const { min, max } = windowFor(action.payload.level, state.player.contrastLevel, levelLimit);
            return {
                ...state,
                player: {
                    ...state.player,
                    ...recomputeRange(min, max, levelLimit),
                },
            };
        }
        case SettingsActionTypes.CHANGE_CONTRAST_LEVEL: {
            const { levelLimit } = state.player;
            const { min, max } = windowFor(state.player.brightnessLevel, action.payload.level, levelLimit);
            return {
                ...state,
                player: {
                    ...state.player,
                    ...recomputeRange(min, max, levelLimit),
                },
            };
        }
        case SettingsActionTypes.CHANGE_DISPLAY_CURVE: {
            return {
                ...state,
                player: { ...state.player, displayCurve: action.payload.curve },
            };
        }
        case SettingsActionTypes.CHANGE_CURVE_PARAMETER: {
            const { displayCurve } = state.player;
            if (displayCurve === 'linear') return state;
            return {
                ...state,
                player: {
                    ...state.player,
                    ...(displayCurve === 'gamma' ?
                        { gammaLevel: action.payload.parameter } :
                        { asinhSoftness: action.payload.parameter }),
                },
            };
        }
        case SettingsActionTypes.SET_DISPLAY_WINDOW: {
            const { min, max, parameter } = action.payload;
            const { displayCurve } = state.player;
            const curveUpdate: Partial<PlayerSettingsState> = {};
            if (typeof parameter === 'number' && displayCurve === 'gamma') curveUpdate.gammaLevel = parameter;
            if (typeof parameter === 'number' && displayCurve === 'asinh') curveUpdate.asinhSoftness = parameter;
            return {
                ...state,
                player: {
                    ...state.player,
                    ...curveUpdate,
                    ...recomputeRange(min, max, state.player.levelLimit),
                },
            };
        }
        case SettingsActionTypes.SET_FRAME_DISPLAY: {
            const { limit, display } = action.payload;
            return {
                ...state,
                player: {
                    ...state.player,
                    levelLimit: limit,
                    displayCurve: display.curve,
                    gammaLevel: display.gammaLevel,
                    asinhSoftness: display.asinhSoftness,
                    ...recomputeRange(display.min, display.max, limit),
                },
            };
        }
        case SettingsActionTypes.CHANGE_MIN_LEVEL: {
            return {
                ...state,
                player: {
                    ...state.player,
                    ...recomputeRange(action.payload.level, state.player.maxLevel, state.player.levelLimit),
                },
            };
        }
        case SettingsActionTypes.CHANGE_MAX_LEVEL: {
            return {
                ...state,
                player: {
                    ...state.player,
                    ...recomputeRange(state.player.minLevel, action.payload.level, state.player.levelLimit),
                },
            };
        }
        case SettingsActionTypes.SWITCH_AUTO_SAVE: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    autoSave: action.payload.autoSave,
                },
            };
        }
        case SettingsActionTypes.CHANGE_AUTO_SAVE_INTERVAL: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    autoSaveInterval: action.payload.autoSaveInterval,
                },
            };
        }
        case SettingsActionTypes.CHANGE_AAM_ZOOM_MARGIN: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    aamZoomMargin: action.payload.aamZoomMargin,
                },
            };
        }
        case SettingsActionTypes.SWITCH_SHOWNIG_INTERPOLATED_TRACKS: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    showAllInterpolationTracks: action.payload.showAllInterpolationTracks,
                },
            };
        }
        case SettingsActionTypes.SWITCH_SHOWING_OBJECTS_TEXT_ALWAYS: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    showObjectsTextAlways: action.payload.showObjectsTextAlways,
                },
            };
        }
        case SettingsActionTypes.SWITCH_AUTOMATIC_BORDERING: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    automaticBordering: action.payload.automaticBordering,
                },
            };
        }
        case SettingsActionTypes.SWITCH_INTELLIGENT_POLYGON_CROP: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    intelligentPolygonCrop: action.payload.intelligentPolygonCrop,
                },
            };
        }
        case SettingsActionTypes.CHANGE_CANVAS_BACKGROUND_COLOR: {
            return {
                ...state,
                player: {
                    ...state.player,
                    canvasBackgroundColor: action.payload.color,
                },
            };
        }
        case SettingsActionTypes.CHANGE_DEFAULT_APPROX_POLY_THRESHOLD: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    defaultApproxPolyAccuracy: action.payload.approxPolyAccuracy,
                },
            };
        }
        case SettingsActionTypes.SWITCH_TOOLS_BLOCKER_STATE: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    toolsBlockerState: { ...state.workspace.toolsBlockerState, ...action.payload.toolsBlockerState },
                },
            };
        }
        case SettingsActionTypes.SWITCH_SETTINGS_DIALOG: {
            return {
                ...state,
                showDialog: action.payload.visible,
            };
        }
        case SettingsActionTypes.SET_SETTINGS: {
            const merged = {
                ...state,
                ...action.payload.settings,
            };
            if (!merged.player) return merged;
            // Brightness and Contrast are two views of the Min/Max window, and a clientSettings
            // entry written before they were flipped to ImageJ's direction carries the old pair.
            // Re-derive both from the window, which is what actually gets rendered, so restored
            // settings can never disagree with the image on screen.
            return {
                ...merged,
                player: {
                    ...merged.player,
                    ...recomputeRange(merged.player.minLevel, merged.player.maxLevel, merged.player.levelLimit),
                },
            };
        }
        case SettingsActionTypes.SWITCH_SHOWING_DELETED_FRAMES: {
            return {
                ...state,
                player: {
                    ...state.player,
                    showDeletedFrames: action.payload.showDeletedFrames,
                },
            };
        }
        case SettingsActionTypes.SWITCH_SHOWING_TAGS_ON_FRAME: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    showTagsOnFrame: action.payload.showTagsOnFrame,
                },
            };
        }
        case SettingsActionTypes.ENABLE_IMAGE_FILTER: {
            const { filter, options } = action.payload;
            const { alias } = filter;
            const filters = [...state.imageFilters];
            const index = filters.findIndex((imageFilter) => imageFilter.alias === alias);
            if (options && index !== -1) {
                const enabledFilter = filters[index];
                enabledFilter.modifier.currentProcessedImage = null;
                enabledFilter.modifier.configure(options);
                return {
                    ...state,
                    imageFilters: filters,
                };
            }
            return {
                ...state,
                imageFilters: [
                    ...state.imageFilters,
                    action.payload.filter,
                ],
            };
        }
        case SettingsActionTypes.DISABLE_IMAGE_FILTER: {
            const { filterAlias } = action.payload;
            const filters = [...state.imageFilters];
            const index = filters.findIndex((imageFilter) => imageFilter.alias === filterAlias);
            if (index !== -1) {
                filters.splice(index, 1);
            }
            filters.forEach((imageFilter) => {
                imageFilter.modifier.currentProcessedImage = null;
            });
            return {
                ...state,
                imageFilters: filters,
            };
        }
        case SettingsActionTypes.CHANGE_LEVEL_LIMIT: {
            const { limit } = action.payload;
            return {
                ...state,
                player: {
                    ...state.player,
                    levelLimit: limit,
                    // Re-clamp: a window of 120..2000 camera counts is meaningless once the frame
                    // on screen is an ordinary 8-bit one, and would render it almost black.
                    ...recomputeRange(state.player.minLevel, state.player.maxLevel, limit),
                },
            };
        }
        case SettingsActionTypes.RESET_IMAGE_FILTERS: {
            return {
                ...state,
                imageFilters: [],
            };
        }
        case AnnotationActionTypes.UPLOAD_JOB_ANNOTATIONS_SUCCESS:
        case AnnotationActionTypes.CREATE_ANNOTATIONS_SUCCESS:
        case AnnotationActionTypes.CHANGE_FRAME_SUCCESS: {
            const { states } = action.payload;
            if (states.some((_state: ObjectState): boolean => _state.shapeType === ShapeType.MASK)) {
                const MIN_OPACITY = 30;
                const { shapes: { opacity } } = state;
                if (opacity < MIN_OPACITY) {
                    return {
                        ...state,
                        shapes: {
                            ...state.shapes,
                            opacity: MIN_OPACITY,
                            selectedOpacity: MIN_OPACITY * 2,
                        },
                    };
                }
            }

            return state;
        }
        case BoundariesActionTypes.RESET_AFTER_ERROR:
        case AnnotationActionTypes.GET_JOB_SUCCESS: {
            const { job } = action.payload;

            return {
                ...state,
                player: {
                    ...state.player,
                },
                shapes: {
                    ...defaultState.shapes,
                    ...(job.dimension === DimensionType.DIMENSION_3D ?
                        {
                            opacity: 40,
                            selectedOpacity: 60,
                        } :
                        {}),
                },
                imageFilters: [],
            };
        }
        case AnnotationActionTypes.INTERACT_WITH_CANVAS: {
            return {
                ...state,
                workspace: {
                    ...state.workspace,
                    toolsBlockerState: {
                        buttonVisible: true,
                        algorithmsLocked: false,
                    },
                },
            };
        }
        case AnnotationActionTypes.CHANGE_WORKSPACE: {
            return {
                ...state,
                shapes: {
                    ...state.shapes,
                    showGroundTruth: false,
                },
            };
        }
        case AuthActionTypes.LOGOUT_SUCCESS: {
            return { ...defaultState };
        }
        default: {
            return state;
        }
    }
};
